import React, { useEffect, useState } from 'react'
import * as S from './EditDatasetDescription.style'

import { formatDateTime } from 'helpers/formatDate/formatDate'
import { CButton } from 'tabs/data/components/CButton'
import { MAX_TEXT_LENGTH } from 'tabs/data/constants'
import { Prompt } from 'react-router-dom'

type EditDatasetDescriptionProps = {
  text: string
  updatedAt?: number
  cancelButtonLabel?: string
  submitButtonLabel?: string
  onSubmit: (text: string) => Promise<void>
  onClose: () => void
}

export const EditDatasetDescription: React.FC<EditDatasetDescriptionProps> = ({
  text: taxtValue = '',
  updatedAt,
  cancelButtonLabel,
  submitButtonLabel = 'Save',

  onClose,
  onSubmit,
}) => {
  const [text, setText] = useState(taxtValue)
  const [errorText, setErrorText] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const [loading, setLoading] = useState(false)

  const handleTextChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value = '' } = e.target
    let errorText = ''

    if (value.length > MAX_TEXT_LENGTH) {
      errorText = `You've reached the max length for the text value — ${MAX_TEXT_LENGTH} characters`
    } else if (value.trim().length < 1) {
      errorText = 'The field is required'
    }

    setErrorText(errorText)
    setText(value)
  }

  const textHasChanges = taxtValue.trim() !== text.trim()

  const formError = errorText || errorAPI

  const isSubmitButtonDisabled = loading || !text.trim() || formError

  const handleSubmit = async () => {
    const isValidForm = !errorText && text
    if (isValidForm && !loading) {
      try {
        setLoading(true)
        await onSubmit(text.trim())
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (textHasChanges) window.onbeforeunload = () => true
    if (!textHasChanges) window.onbeforeunload = () => null

    return () => {
      window.onbeforeunload = null
    }
  }, [textHasChanges, isSubmitButtonDisabled])

  return (
    <S.Container>
      <Prompt
        when={textHasChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />

      <S.Form>
        <S.InputContainer isError={Boolean(formError)}>
          <S.Textarea
            autoFocus
            disabled={loading}
            placeholder="+ Insert text here"
            value={text}
            onChange={handleTextChange}
            isError={Boolean(formError)}
          />
          <S.Error>{formError}</S.Error>
        </S.InputContainer>

        <S.Footer>
          {updatedAt && (
            <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {formatDateTime(updatedAt * 1000)}</small>
          )}
          <CButton color="secondary" disabled={loading} onClick={onClose}>
            {cancelButtonLabel || 'Cancel'}
          </CButton>
          <CButton disabled={Boolean(textHasChanges || isSubmitButtonDisabled)} onClick={handleSubmit}>
            {submitButtonLabel}
          </CButton>
        </S.Footer>
      </S.Form>
    </S.Container>
  )
}
